import { STORE_CODES } from "@nova-ai/shared"
import { PRODUCT_VISUAL_KINDS } from "@nova-ai/product-visual-templates/kinds"
import { createFileRoute, Link } from "@tanstack/react-router"
import { History, RefreshCw } from "lucide-react"

import { AppShell } from "../components/layout/app-shell"
import { Button } from "../components/ui/button"
import { STUDIO_HISTORY_SOURCE_KINDS } from "../components/pim-workflow-history/workflow-history-context"
import { WorkflowHistoryFilters } from "../components/pim-workflow-history/workflow-history-filters"
import { WorkflowHistoryResults } from "../components/pim-workflow-history/workflow-history-results"
import { formatDateTime } from "../lib/format-date"
import {
  usePimWorkflowHistory,
  workflowHistorySearchSchema,
  type WorkflowHistorySearch,
} from "../services/use-pim-workflow-history"

function WorkflowHistoryPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const history = usePimWorkflowHistory(search)
  const { list, detail, refresh } = history
  const observedAt =
    search.receiptId === undefined ? list.data?.observedAt : detail.data?.observedAt
  const onChange = (next: WorkflowHistorySearch) => {
    void navigate({ search: next })
  }
  return (
    <AppShell title="Workflow history" icon={<History className="size-5" />} fullWidth>
      <div className="space-y-6">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div className="space-y-2">
            <Link
              to="/studio"
              search={{
                store: STORE_CODES.find((store) => store === search.store),
                kind: PRODUCT_VISUAL_KINDS.find(
                  (kind) => STUDIO_HISTORY_SOURCE_KINDS[kind] === search.kind
                ),
              }}
              className="rounded text-sm text-info hover:underline focus-visible:ring-2 focus-visible:ring-ring"
            >
              ← Back to Studio
            </Link>

            <p className="text-sm text-muted-foreground">
              Follow PIM receipts through image generation and publication.
            </p>
          </div>

          <div className="space-y-1 text-right">
            <Button onClick={refresh} disabled={list.isFetching || detail.isFetching}>
              <RefreshCw className="size-4" />
              Refresh
            </Button>

            <p className="text-xs text-muted-foreground">
              {observedAt ? `Last refreshed ${formatDateTime(observedAt)}` : "Not refreshed yet"}
            </p>
          </div>
        </header>

        <WorkflowHistoryFilters key={JSON.stringify(search)} search={search} onChange={onChange} />

        <p className="text-xs text-muted-foreground">
          Active work refreshes every 15 seconds for up to five minutes. Manual refresh restarts
          updates.
        </p>

        <WorkflowHistoryResults history={history} search={search} onChange={onChange} />
      </div>
    </AppShell>
  )
}

export const Route = createFileRoute("/studio_/history")({
  validateSearch: workflowHistorySearchSchema,
  component: WorkflowHistoryPage,
  errorComponent: () => (
    <AppShell title="Workflow history">
      <p role="alert" className="mb-4 text-sm">
        These history filters are invalid. Clear them to continue.
      </p>

      <Link to="/studio/history" search={{ limit: 25 }} className="text-info hover:underline">
        Clear filters
      </Link>
    </AppShell>
  ),
})
